import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useAltHotkeys } from '../hooks/useAltHotkeys';
import { useModalDrag } from '../hooks/useModalDrag';
import ModalHeader from './ui/ModalHeader';

interface Workspace {
  id: string;
  name: string;
  order: number;
  columnCount: number;
}

interface DuplicateCardModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (newName: string, workspaceId: string, createNewWorkspace: boolean) => void;
  originalCardName: string;
  workspaces: Workspace[];
  currentWorkspaceId: string;
  maxCardsPerWorkspace?: number;
  getWorkspaceCardCount: (workspaceId: string) => number;
  /** Optional: workspaces that can't receive a duplicated card (protected
   *  system/panel workspaces, e.g. LC's Main / Exports / Accounting). They are
   *  shown disabled and never auto-selected. Defaults to none. */
  isWorkspaceProtected?: (workspaceId: string) => boolean;
}

export default function DuplicateCardModal({
  isOpen,
  onClose,
  onSubmit,
  originalCardName,
  workspaces,
  currentWorkspaceId,
  getWorkspaceCardCount,
  maxCardsPerWorkspace = 8,
  isWorkspaceProtected = () => false,
}: DuplicateCardModalProps) {
  const [newName, setNewName] = useState<string>(`${originalCardName} - Copy`);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string>(currentWorkspaceId);
  const [isNewWorkspace, setIsNewWorkspace] = useState<boolean>(false);
  const wasOpen = useRef(false);

  // Initialize once per opening. Fresh callbacks or workspace data from the
  // dashboard must not overwrite the user's selection or name while editing.
  useEffect(() => {
    const justOpened = isOpen && !wasOpen.current;
    wasOpen.current = isOpen;
    if (!justOpened) return;

    if (isOpen) {
      setNewName(`${originalCardName} - Copy`);

      // A workspace can't take a duplicated card when it's full OR it's a
      // protected system/panel workspace (e.g. LC's Main / Exports / Accounting).
      const isUnavailable = (workspaceId: string) =>
        isWorkspaceProtected(workspaceId) || getWorkspaceCardCount(workspaceId) >= maxCardsPerWorkspace;

      if (isUnavailable(currentWorkspaceId)) {
        // Current workspace can't receive the card — pick the first that can.
        const availableWorkspace = [...workspaces]
          .sort((a, b) => a.order - b.order)
          .find(w => !isUnavailable(w.id));

        if (availableWorkspace) {
          setSelectedWorkspaceId(availableWorkspace.id);
          setIsNewWorkspace(false);
        } else {
          // Nowhere available — default to "New Workspace".
          setIsNewWorkspace(true);
          setSelectedWorkspaceId(currentWorkspaceId);
        }
      } else {
        setSelectedWorkspaceId(currentWorkspaceId);
        setIsNewWorkspace(false);
      }
    }
  }, [isOpen, originalCardName, currentWorkspaceId, workspaces, getWorkspaceCardCount, isWorkspaceProtected, maxCardsPerWorkspace]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName.trim()) return;

    onSubmit(newName.trim(), selectedWorkspaceId, isNewWorkspace);
  };

  const handleWorkspaceChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (value === '__new__') {
      setIsNewWorkspace(true);
      setSelectedWorkspaceId(currentWorkspaceId); // Will be overridden when new workspace is created
    } else {
      setIsNewWorkspace(false);
      setSelectedWorkspaceId(value);
    }
  };

  // Alt+C closes. No Alt+S: the primary action is a native form submit, so Enter
  // already does it. The layer keeps Alt+S off the dashboard behind this.
  useAltHotkeys({ C: onClose }, undefined, { layer: isOpen });

  const { panelRef, panelStyle, dragHandleProps } = useModalDrag(isOpen);

  if (!isOpen) return null;

  // Portal to <body> so the modal is a top-level sibling of the LC bucket drawer
  // (itself a z-[100] body portal). Rendered inline, a dashboard ancestor's
  // stacking context would trap it below the drawer regardless of z-index.
  return createPortal(
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-scrim bg-opacity-50">
      <div
        ref={panelRef}
        style={panelStyle}
        className="bg-panel rounded-lg shadow-xl w-full max-w-md mx-4"
      >
        <ModalHeader title="Duplicate Table" onClose={onClose} dragHandleProps={dragHandleProps} />

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Name Field */}
          <div>
            <label
              htmlFor="cardName"
              className="block text-sm font-medium text-label mb-1"
            >
              Name
            </label>
            <input
              type="text"
              id="cardName"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
              placeholder="Enter table name"
              autoFocus
              required
            />
          </div>

          {/* Workspace Dropdown */}
          <div>
            <label
              htmlFor="workspace"
              className="block text-sm font-medium text-label mb-1"
            >
              Workspace
            </label>
            <select
              id="workspace"
              value={isNewWorkspace ? '__new__' : selectedWorkspaceId}
              onChange={handleWorkspaceChange}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            >
              {[...workspaces]
                .sort((a, b) => a.order - b.order)
                .map((workspace) => {
                  const cardCount = getWorkspaceCardCount(workspace.id);
                  const isProtected = isWorkspaceProtected(workspace.id);
                  const isFull = cardCount >= maxCardsPerWorkspace;
                  return (
                    <option
                      key={workspace.id}
                      value={workspace.id}
                      disabled={isProtected || isFull}
                    >
                      {workspace.name}{isProtected ? ' (System)' : ` (${cardCount}/${maxCardsPerWorkspace})${isFull ? ' - Full' : ''}`}
                    </option>
                  );
                })}
              <option value="__new__">New Workspace</option>
            </select>
            {isNewWorkspace && (
              <p className="mt-1 text-xs text-muted">
                A new workspace will be created and opened
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Duplicate
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}
